import type { SimulateEventsResponse } from '@monorepo/shared/schemas/events'
import { useInfiniteQuery } from '@tanstack/react-query'
import type { ColumnDef } from '@tanstack/react-table'
import { Loader2, RefreshCw, Zap } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { VirtualizedTable } from '@/shared/components/table/VirtualizedTable'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import {
  eventResponseToSignal,
  eventsService,
  timeRangeToFromDate,
} from '../services/eventsService'
import type {
  Signal,
  SignalFilters as SignalFiltersType,
} from '../types/events.types'
import { SignalActionMenu } from './SignalActionMenu'
import { SignalDetailsDrawer } from './SignalDetailsDrawer'
import { SignalFilters } from './SignalFilters'

export function SignalsLiveTab() {
  const [selectedSignal, setSelectedSignal] = useState<Signal | null>(null)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [filters, setFilters] = useState<SignalFiltersType>({
    time_range: 'last_24h',
  })

  // Simulation state
  const [simulateCount, setSimulateCount] = useState(50)
  const [isSimulating, setIsSimulating] = useState(false)
  const [lastSimResult, setLastSimResult] =
    useState<SimulateEventsResponse | null>(null)

  const fromDate = timeRangeToFromDate(filters.time_range, filters.custom_from)

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey: ['events', 'live', filters],
    queryFn: ({ pageParam = 1 }) =>
      eventsService.getEvents({
        page: pageParam as number,
        limit: 50,
        event_name: filters.signal_name || undefined,
        customer_id: filters.customer_id || undefined,
        from_date: fromDate,
        to_date: filters.custom_to || undefined,
        sort: 'timestamp',
        order: 'desc',
      }),
    initialPageParam: 1,
    getNextPageParam: lastPage =>
      lastPage.pagination.has_next ? lastPage.pagination.page + 1 : undefined,
    staleTime: 30 * 1000,
  })

  const signals =
    data?.pages.flatMap(page => page.data.map(eventResponseToSignal)) ?? []
  const total = data?.pages[0]?.pagination.total ?? 0

  const handleSimulate = async () => {
    if (simulateCount < 1 || simulateCount > 500) {
      return
    }
    setIsSimulating(true)
    setLastSimResult(null)
    try {
      const result = await eventsService.simulateEvents(simulateCount)
      setLastSimResult(result)
      toast.success(`Simulated ${result.simulated} events`)
    } catch (err) {
      const message =
        err !== null &&
        typeof err === 'object' &&
        'response' in err &&
        err.response !== null &&
        typeof err.response === 'object' &&
        'data' in err.response &&
        err.response.data !== null &&
        typeof err.response.data === 'object' &&
        'error' in err.response.data &&
        err.response.data.error !== null &&
        typeof err.response.data.error === 'object' &&
        'message' in err.response.data.error
          ? String(err.response.data.error.message)
          : 'Simulation failed'
      toast.error(message)
    } finally {
      setIsSimulating(false)
    }
  }

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  }

  const handleRowClick = (signal: Signal) => {
    setSelectedSignal(signal)
    setIsDrawerOpen(true)
  }

  const columns: ColumnDef<Signal>[] = [
    {
      accessorKey: 'signal_name',
      header: 'Event Name',
      cell: ({ row }) => (
        <div className='text-sm'>
          <div className='font-medium'>{row.original.signal_name}</div>
        </div>
      ),
      size: 370,
    },
    {
      id: 'customer_id',
      header: 'Customer ID',
      cell: ({ row }) => (
        <div className='text-sm'>
          <div className='font-medium font-mono text-xs'>
            {row.original.customer_id || '-'}
          </div>
        </div>
      ),
      size: 250,
    },
    {
      id: 'agent_id',
      header: 'Agent ID',
      cell: ({ row }) => (
        <div className='text-sm'>
          {row.original.agent_id ? (
            <div className='font-medium font-mono text-xs'>
              {row.original.agent_id}
            </div>
          ) : (
            <div className='text-xs text-muted-foreground'>-</div>
          )}
        </div>
      ),
      size: 225,
    },
    {
      accessorKey: 'signal_type',
      header: 'Signal Type',
      cell: ({ row }) => (
        <div className='text-sm'>
          <div className='font-medium'>
            {row.original.signal_type === 'cost_signal'
              ? 'Cost Signal'
              : 'Billable Signal'}
          </div>
        </div>
      ),
      size: 180,
    },
    {
      accessorKey: 'timestamp',
      header: 'Timestamp',
      cell: ({ row }) => (
        <div className='text-sm'>
          <div className='font-medium'>
            {formatDateTime(row.original.timestamp)}
          </div>
          <div className='text-xs text-muted-foreground'>
            {new Date(row.original.timestamp).toLocaleDateString()}
          </div>
        </div>
      ),
      size: 225,
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <SignalActionMenu
          signal={row.original}
          onViewDetails={handleRowClick}
        />
      ),
      size: 60,
    },
  ]

  return (
    <div className='space-y-6'>
      {/* Simulation Panel */}
      <div className='rounded-lg border bg-card p-4'>
        <div className='flex items-center gap-2 mb-3'>
          <Zap className='w-4 h-4 text-amber-500' />
          <span className='text-sm font-medium'>Simulate Events</span>
          <span className='text-xs text-muted-foreground ml-1'>
            Generate random events using your tenant's agents and customers
          </span>
        </div>
        <div className='flex items-center gap-3'>
          <div className='flex items-center gap-2'>
            <Label
              htmlFor='sim-count'
              className='text-sm text-muted-foreground whitespace-nowrap'
            >
              Count
            </Label>
            <Input
              id='sim-count'
              type='number'
              min={1}
              max={500}
              value={simulateCount}
              onChange={e =>
                setSimulateCount(
                  Math.min(500, Math.max(1, Number(e.target.value) || 1))
                )
              }
              className='w-20 h-8 text-sm'
            />
          </div>
          <Button
            size='sm'
            className='h-8'
            onClick={handleSimulate}
            disabled={isSimulating}
          >
            {isSimulating ? (
              <>
                <Loader2 className='w-3 h-3 mr-2 animate-spin' />
                Simulating...
              </>
            ) : (
              <>
                <Zap className='w-3 h-3 mr-2' />
                Simulate
              </>
            )}
          </Button>
          {lastSimResult && (
            <span className='text-xs text-muted-foreground'>
              ✓ {lastSimResult.simulated} events sent across{' '}
              {lastSimResult.agent_count} agent
              {lastSimResult.agent_count !== 1 ? 's' : ''},{' '}
              {lastSimResult.customer_count} customer
              {lastSimResult.customer_count !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>

      <SignalFilters filters={filters} onFiltersChange={setFilters} />

      <div className='flex justify-between items-center'>
        <div className='text-sm text-muted-foreground'>
          {total} event{total !== 1 ? 's' : ''} found
        </div>
        <Button
          variant='outline'
          size='sm'
          onClick={() => refetch()}
          disabled={isLoading}
        >
          <RefreshCw
            className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`}
          />
          Refresh
        </Button>
      </div>

      <div className='[&_table]:bg-transparent [&_thead]:bg-transparent [&_tr:hover]:bg-slate-50/50 dark:[&_tr:hover]:bg-slate-800/50'>
        <VirtualizedTable
          data={signals}
          columns={columns}
          isLoading={isLoading}
          isFetchingNextPage={isFetchingNextPage}
          hasNextPage={!!hasNextPage}
          fetchNextPage={fetchNextPage}
          containerHeight='calc(100vh - 560px)'
          emptyMessage='No events found. Simulate events or wait for your SDKs to send data.'
          loadingMessage='Loading more events...'
          onRowClick={handleRowClick}
          enableSorting={true}
          enableColumnResizing={true}
          enableColumnReordering={true}
          enableColumnPinning={false}
        />
      </div>

      <SignalDetailsDrawer
        signal={selectedSignal}
        isOpen={isDrawerOpen}
        onOpenChange={setIsDrawerOpen}
        onViewDetails={handleRowClick}
      />
    </div>
  )
}
